/**
 * Daily Missions Component
 * Displays today's missions with progress tracking
 */

import React, { useState, useEffect } from 'react';
import { gamificationService } from '../services/gamificationService';
import { CheckCircle2, Circle, Clock, Star, Sparkles, Trophy, Zap } from 'lucide-react';
import { Button } from './ui/Button';

const RARITY_CONFIG = {
  common: {
    icon: Star,
    color: 'text-gray-600',
    bg: 'bg-gray-100',
    border: 'border-gray-300'
  },
  rare: {
    icon: Sparkles,
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    border: 'border-blue-300'
  },
  epic: {
    icon: Trophy,
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    border: 'border-purple-300'
  },
  legendary: {
    icon: Zap,
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    border: 'border-yellow-300'
  }
};

export default function DailyMissions({ onComplete }) {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeUntilReset, setTimeUntilReset] = useState('');

  useEffect(() => {
    loadMissions();
    
    // Update countdown timer
    const interval = setInterval(() => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const diff = tomorrow - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      setTimeUntilReset(`${hours}h ${minutes}m`);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const loadMissions = async () => {
    try {
      const data = await gamificationService.getTodaysMissions();
      setMissions(data);
    } catch (error) {
      console.error('Failed to load missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressPercent = (mission) => {
    if (!mission.progress || !mission.requirements) return 0;
    
    // Calculate average progress across all requirements
    const reqs = Object.entries(mission.requirements);
    const totalProgress = reqs.reduce((sum, [key, required]) => {
      const current = mission.progress[key] || 0;
      return sum + Math.min((current / required) * 100, 100);
    }, 0);
    
    return Math.round(totalProgress / reqs.length);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse bg-gray-100 rounded-lg h-24" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Daily Missions</h3>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span>Resets in {timeUntilReset}</span>
        </div>
      </div>

      {/* Mission cards */}
      <div className="space-y-3">
        {missions.map((mission) => {
          const progress = getProgressPercent(mission);
          const RarityIcon = RARITY_CONFIG[mission.rarity]?.icon || Star;
          const rarityConfig = RARITY_CONFIG[mission.rarity] || RARITY_CONFIG.common;

          return (
            <div
              key={mission.id}
              className={`bg-white rounded-lg border-2 ${rarityConfig.border} p-4 transition-all hover:shadow-md`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`${rarityConfig.bg} ${rarityConfig.color} p-1.5 rounded`}>
                      <RarityIcon className="w-4 h-4" />
                    </div>
                    <h4 className="font-semibold text-gray-900">{mission.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{mission.description}</p>
                </div>

                {mission.isCompleted ? (
                  <div className="flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">+{mission.xp_reward} XP</span>
                  </div>
                ) : (
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-900">+{mission.xp_reward} XP</div>
                  </div>
                )}
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{mission.isCompleted ? 'Complete!' : 'In Progress'}</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      mission.isCompleted
                        ? 'bg-green-500'
                        : `bg-gradient-to-r ${rarityConfig.color === 'text-gray-600' ? 'from-gray-400 to-gray-500' : rarityConfig.color === 'text-blue-600' ? 'from-blue-400 to-blue-500' : rarityConfig.color === 'text-purple-600' ? 'from-purple-400 to-purple-500' : 'from-yellow-400 to-yellow-500'}`
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Requirements breakdown */}
              {mission.requirements && !mission.isCompleted && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                  {Object.entries(mission.requirements).map(([key, required]) => {
                    const current = mission.progress?.[key] || 0;
                    return (
                      <div key={key} className="flex items-center justify-between text-xs">
                        <span className="text-gray-600 capitalize">{key.replace('_', ' ')}</span>
                        <span className="font-medium text-gray-900">
                          {current} / {required}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-600">Missions Completed</div>
            <div className="text-2xl font-bold text-gray-900">
              {missions.filter(m => m.isCompleted).length} / {missions.length}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Total XP Available</div>
            <div className="text-2xl font-bold text-blue-600">
              {missions.reduce((sum, m) => sum + (m.isCompleted ? 0 : m.xp_reward), 0)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
